import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  BiBriefcase,
  BiGroup,
  BiFile,
  BiDollar,
  BiTimeFive,
  BiCreditCard
} from "react-icons/bi";

import { API_BASE_URL } from "../utils/urls.js";
import axiosClient from "../utils/apiClient.js";
import { BiRocket } from 'react-icons/bi';
import OnboardingChecklist from "../Onboarding/OnboardingChecklist";
import AdminWelcomeModal from "../Onboarding/AdminWelcomeModal";
import { useOnboarding } from "../Onboarding/OnboardingContext";

const StatCard = ({ title, value, icon, link, colorClass = "text-gray-500" }) => {
  return (
    <Link
      to={link}
      className="block bg-white p-4 sm:p-6 rounded-xl shadow-sm hover:shadow-lg transition-transform hover:-translate-y-1"
    >
      <div className="flex items-center space-x-4">
        {React.cloneElement(icon, { className: `w-10 h-10 ${colorClass}` })}
        <div>
          <p className="text-gray-500 text-sm sm:text-base">{title}</p>
          <p className="text-xl font-semibold text-gray-900 break-words">{value}</p>
        </div>
      </div>
    </Link>
  );
};

const AdminDashboard = () => {
  const { runTour, setRunTour, stepIndex, setStepIndex, showWelcomeModal } = useOnboarding();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [activity, setActivity] = useState([]);
  const [error, setError] = useState(null);
  const [showStripe, setshowStripe] = useState(false);
  const [onboardingProgress, setOnboardingProgress] = useState({
    completed_events: [],
    progress_percentage: 0,
    is_trial_activation_complete: false
  });

  const user = JSON.parse(localStorage.getItem("user"));
  const [isDismissed, setIsDismissed] = useState(() => {
    const cached = user?.id ? localStorage.getItem(`trial_checklist_dismissed_${user.id}`) : null;
    return cached === "true";
  });

  const handleDismissChecklist = async () => {
    setIsDismissed(true);
    if (user?.id) {
      localStorage.setItem(`trial_checklist_dismissed_${user.id}`, "true");
    }
    try {
      await axiosClient.post("/onboarding/dismiss");
    } catch (error) {
      try {
        await axiosClient.post("/onboarding/track", { step_column: "is_dismissed" });
      } catch (err) {
        console.warn("Failed to persist dismissal to server:", err);
      }
    }
  };

  const handleStripeConnect = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await axiosClient.get(`/stripe/connect`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      

      if (res.data?.url) {
        window.location.href = res.data.url; // Redirect to Stripe Connect
      } else {
        alert("Unable to start Stripe connection.");
      }
    } catch (err) {
      console.error("Stripe connect error:", err);
      alert("Failed to connect Stripe account.");
    }
  };

  useEffect(() => {
    const fetchStats = async () => {
      const token = localStorage.getItem("token");
      if (!token) return setError("Authentication required.");

      try {
        const res = await axiosClient.get(`/dashboard/admin/stats`);

       if (!res.data.stripeConnected == 1 || !res.data.stripeAccountId) {
          setshowStripe(true);
          localStorage.removeItem('stripeAccountId');
       }else{
        setshowStripe(false);
          localStorage.setItem('stripeAccountId', res.data.stripeAccountId);
       }
        setStats(res.data.metrics);
        setActivity(res.data.activityLog);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to fetch dashboard data.");
      } finally {
        setLoading(false);
      }
    };

    const fetchOnboardingProgress = async () => {
        try {
            const response = await axiosClient.get('/onboarding/progress-metrics');
            if (response.data) {
                setOnboardingProgress(response.data);
                if (response.data.is_dismissed) {
                    setIsDismissed(true);
                    if (user?.id) {
                        localStorage.setItem(`trial_checklist_dismissed_${user.id}`, "true");
                    }
                }
            }
        } catch (error) {
            console.warn("Could not load onboarding progress.");
        }
    };

    fetchStats();
    fetchOnboardingProgress();
  }, []);

  const isTrialComplete = onboardingProgress?.is_trial_activation_complete;

  if (loading)
    return (
      <div className="flex items-center justify-center h-64">
        <p className="animate-pulse text-gray-600">Loading dashboard...</p>
      </div>
    );

  if (error)
    return (
      <div className="p-6">
        <p className="bg-red-100 border border-red-300 text-red-600 p-4 rounded-lg">
          {error}
        </p>
      </div>
    );

  const formatCurrency = (amt) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
      parseFloat(amt || 0)
    );

  return (
    <div className="p-4 sm:p-6 space-y-8 max-w-7xl mx-auto">


      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
          <h1 className="text-3xl font-bold text-gray-800 dashboard-tour-start">
            Welcome back, {user?.firstName || "Admin"} 👋
          </h1>
      </div>

      {/* Trial Activation Checklist (Horizontal) */}
      {!isDismissed && onboardingProgress && (
        <OnboardingChecklist 
          progress={onboardingProgress} 
          horizontal={true} 
          onDismiss={handleDismissChecklist}
        />
      )}


      {showStripe && (
        <div className="bg-indigo-50 border border-indigo-200 p-4 rounded-lg shadow-sm flex justify-between items-center">
          <div>
            <p className="text-indigo-800 font-semibold">Payments Setup</p>
            <p className="text-sm text-indigo-600">
              Connect your Stripe account to receive invoice payments.
            </p>
          </div>

          <button
            onClick={handleStripeConnect}
            className="flex items-center bg-indigo-600 text-white px-4 py-2 rounded-lg shadow hover:bg-indigo-700 transition"
          >
            <BiCreditCard className="mr-2 h-5 w-5" />
            Connect Stripe
          </button>
        </div>
      )}


      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard title="Clients" value={stats.numberOfClients} icon={<BiGroup />} link="/clients" colorClass="text-blue-500" />
        <StatCard title="Cases" value={stats.numberOfCases} icon={<BiBriefcase />} link="/cases" colorClass="text-purple-500" />
        <StatCard title="Team Members" value={stats.numberOfTeamMembers} icon={<BiGroup />} link="/teams" colorClass="text-green-500" />
        <StatCard title="Unpaid Invoices" value={stats.unpaidInvoicesCount} icon={<BiFile />} link="/invoices" colorClass="text-red-500" />
        <StatCard title="Total Revenue" value={formatCurrency(stats.totalRevenue)} icon={<BiDollar />} link="/invoices" colorClass="text-yellow-500" />
        <StatCard title="Revenue (Last 30 Days)" value={formatCurrency(stats.revenueThisMonth)} icon={<BiTimeFive />} link="/invoices" colorClass="text-indigo-500" />
      </div>

      {/* Activity Log */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Recent Activity</h2>
        <div className="overflow-x-auto">
          <ul className="divide-y divide-gray-200">
            {activity.length > 0 ? (
              activity.map((log) => (
                <li key={log.id} className="py-4 flex flex-col sm:flex-row sm:justify-between sm:items-center">
                  <div>
                    <p className="text-gray-700 font-medium">{log.description}</p>
                    <p className="text-sm text-gray-400">
                      Case: {log.case_number} — {log.case_name}
                    </p>
                  </div>
                  <div className="text-sm text-gray-500 mt-2 sm:mt-0 text-right">
                    {log.user.full_name}
                    <br />
                    <span className="text-xs">{new Date(log.created_at).toLocaleString()}</span>
                  </div>
                </li>
              ))
            ) : (
              <p className="text-gray-400 text-center py-4">No recent activity.</p>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
